/* =================================================================
   Pilatix FAQ — sections/pltx2-faq.liquid behavior

     • Search (filter as you type, strips diacritics and Hebrew niqud)
     • Sticky sidebar active state via IntersectionObserver
     • Click a sidebar link → smooth scroll to the category block
     • All state is DOM-driven; no persistence, no framework
   ================================================================= */
(function () {
  'use strict';

  const ROOT_SELECTOR = '[data-pltx2-faq]';
  const NIQUD_RE = /[\u0591-\u05C7]/g;   // Hebrew cantillation + niqud
  const INIT_FLAG = '__pltx2FaqInit';

  function normalize(s) {
    return (s || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(NIQUD_RE, '')
      .replace(/[^\p{L}\p{N}\s]/gu, ' ')   // strip punctuation, keep Unicode letters
      .replace(/\s+/g, ' ')
      .trim();
  }

  function initFaq(root) {
    if (root[INIT_FLAG]) return;
    root[INIT_FLAG] = true;

    const searchInput = root.querySelector('[data-pltx2-faq-search]');
    const searchClear = root.querySelector('[data-pltx2-faq-clear]');
    const searchEmpty = root.querySelector('[data-pltx2-faq-empty]');
    const navLinks = Array.from(root.querySelectorAll('[data-pltx2-faq-nav-link]'));
    const groups = Array.from(root.querySelectorAll('[data-pltx2-faq-group]'));
    const items = Array.from(root.querySelectorAll('[data-pltx2-faq-item]'));

    // Index each item with its normalized question text + its answer text for matching
    items.forEach(function (item) {
      const q = item.getAttribute('data-q') || '';
      const answerEl = item.querySelector('.pltx2-faq__a');
      const a = answerEl ? answerEl.textContent : '';
      item.__pltx2Haystack = normalize(q + ' ' + a);
    });

    /* ---------- PREMIUM OPEN/CLOSE ANIMATION ----------
       Native <details> hard-cuts on close. We drive the height + opacity
       via CSS transitions (not WAAPI) so cleanup at the end is instant —
       the final frame is the natural layout, no fill-forwards flicker. */
    const EASE = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const DURATION_OPEN = reduceMotion ? 0 : 360;
    const DURATION_CLOSE = reduceMotion ? 0 : 280;

    function animateHeight(el, fromH, toH, duration, onDone) {
      if (duration === 0) { if (onDone) onDone(); return; }
      // Cancel any in-flight anim on this element
      if (el.__pltx2Cleanup) el.__pltx2Cleanup(true);

      el.style.overflow = 'hidden';
      el.style.height = fromH + 'px';
      el.style.opacity = fromH === 0 ? '0' : '1';
      el.style.willChange = 'height, opacity';
      // Force reflow so the starting state commits before we set the end state
      void el.offsetHeight;
      el.style.transition =
        'height ' + duration + 'ms ' + EASE +
        ', opacity ' + Math.round(duration * 0.75) + 'ms ' + EASE;
      el.style.height = toH + 'px';
      el.style.opacity = toH === 0 ? '0' : '1';

      let finished = false;
      function cleanup(aborted) {
        if (finished) return;
        finished = true;
        el.removeEventListener('transitionend', onTransitionEnd);
        clearTimeout(safetyTimer);
        el.style.transition = '';
        el.style.height = '';
        el.style.opacity = '';
        el.style.overflow = '';
        el.style.willChange = '';
        el.__pltx2Cleanup = null;
        if (!aborted && onDone) onDone();
      }
      function onTransitionEnd(e) {
        // Only care about the height transition — opacity ends sooner
        if (e.propertyName !== 'height' || e.target !== el) return;
        cleanup(false);
      }
      el.addEventListener('transitionend', onTransitionEnd);
      // Safety net in case transitionend doesn't fire (hidden tab, etc.)
      const safetyTimer = setTimeout(function () { cleanup(false); }, duration + 80);
      el.__pltx2Cleanup = cleanup;
    }

    items.forEach(function (item) {
      const summary = item.querySelector('.pltx2-faq__summary');
      const answer = item.querySelector('.pltx2-faq__a');
      if (!summary || !answer) return;

      let animating = false;

      summary.addEventListener('click', function (e) {
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
        e.preventDefault();
        if (animating) return;
        animating = true;

        if (item.open) {
          // CLOSE: measure current, collapse to 0, then toggle open=false.
          // Keep height:0 inline styles until AFTER open=false so the
          // element can't briefly re-expand to natural height between
          // cleanup clearing styles and <details> going display:none.
          const startH = answer.scrollHeight;
          animateHeight(answer, startH, 0, DURATION_CLOSE, function () {
            answer.style.height = '0px';
            answer.style.overflow = 'hidden';
            answer.style.opacity = '0';
            item.open = false;
            // Next frame: details is now display:none, safe to clear inline
            requestAnimationFrame(function () {
              answer.style.height = '';
              answer.style.overflow = '';
              answer.style.opacity = '';
              animating = false;
            });
          });
        } else {
          // OPEN: toggle first so children render, measure natural height,
          // then animate from 0 to that height
          item.open = true;
          const endH = answer.scrollHeight;
          animateHeight(answer, 0, endH, DURATION_OPEN, function () {
            animating = false;
          });
        }
      });
    });

    /* ---------- SEARCH ---------- */
    const emptyEcho = root.querySelector('[data-pltx2-faq-empty-q]');

    function applyFilter(rawQuery) {
      const query = normalize(rawQuery);

      if (searchClear) {
        searchClear.hidden = !rawQuery;
      }

      if (!query) {
        // Reset everything
        items.forEach(function (it) { it.hidden = false; });
        groups.forEach(function (g) { g.hidden = false; });
        if (searchEmpty) searchEmpty.hidden = true;
        return;
      }

      let anyMatch = false;
      groups.forEach(function (group) {
        const groupItems = group.querySelectorAll('[data-pltx2-faq-item]');
        let groupHasMatch = false;
        groupItems.forEach(function (item) {
          const match = item.__pltx2Haystack && item.__pltx2Haystack.indexOf(query) !== -1;
          item.hidden = !match;
          if (match) groupHasMatch = true;
        });
        group.hidden = !groupHasMatch;
        if (groupHasMatch) anyMatch = true;
      });

      if (searchEmpty) {
        searchEmpty.hidden = anyMatch;
        if (!anyMatch && emptyEcho) {
          emptyEcho.textContent = '"' + rawQuery.trim() + '"';
        }
      }

      // Group visibility changed — re-derive the active chip
      if (root.__pltx2FaqRecompute) root.__pltx2FaqRecompute();
    }

    // Clear the search + filter state (used by nav-link clicks so the
    // target category is actually visible to scroll to).
    function resetSearch() {
      if (searchInput && searchInput.value) {
        searchInput.value = '';
      }
      applyFilter('');
    }

    if (searchInput) {
      let debounceTimer;
      searchInput.addEventListener('input', function (e) {
        clearTimeout(debounceTimer);
        const value = e.target.value;
        debounceTimer = setTimeout(function () {
          applyFilter(value);
        }, 80);
      });

      // Clear on Escape
      searchInput.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && searchInput.value) {
          e.preventDefault();
          searchInput.value = '';
          applyFilter('');
        }
      });
    }

    if (searchClear) {
      searchClear.addEventListener('click', function () {
        if (!searchInput) return;
        searchInput.value = '';
        applyFilter('');
        searchInput.focus();
      });
    }

    /* ---------- STICKY BARS MEASUREMENT + ACTIVE CHIP TRACKING ----------
       The theme site header is position:sticky and always pinned. Its live
       bottom edge is exported as --pltx-theme-header-h (same variable the
       collection page uses) so the mobile chip bar can stick FLUSH under it,
       and the chip bar's own height is exported as --pltx2-faq-chips-h for
       the groups' scroll-margin. Active state is computed from a "reading
       line" just below the sticky bars — deterministic at both extremes
       (IntersectionObserver bands could never activate a short last group). */
    const themeHeader =
      document.querySelector('.shopify-section-group-header-group') ||
      document.querySelector('sticky-header') ||
      document.querySelector('header.header');
    const sidebar = root.querySelector('.pltx2-faq__sidebar');
    const nav = root.querySelector('.pltx2-faq__nav');

    // Matches the CSS mobile breakpoint — cheaper than probing computed
    // styles every frame during scroll.
    const chipModeMq = window.matchMedia('(max-width: 749px)');
    function isChipMode() {
      return !!nav && chipModeMq.matches;
    }

    let lastHeaderVar = '';
    let lastChipsVar = '';
    function syncOverlayVars() {
      if (themeHeader) {
        // Unrounded: rounding to whole pixels opened a subpixel seam between
        // the header and the pinned chip bar on 2x/3x mobile screens.
        const hb = Math.max(0, themeHeader.getBoundingClientRect().bottom).toFixed(2) + 'px';
        if (hb !== lastHeaderVar) {
          lastHeaderVar = hb;
          document.documentElement.style.setProperty('--pltx-theme-header-h', hb);
        }
      }
      if (sidebar) {
        const sh = sidebar.getBoundingClientRect().height.toFixed(2) + 'px';
        if (sh !== lastChipsVar) {
          lastChipsVar = sh;
          document.documentElement.style.setProperty('--pltx2-faq-chips-h', sh);
        }
      }
    }

    if (navLinks.length && groups.length) {
      // Map group id → nav link for O(1) lookup
      const linkById = {};
      navLinks.forEach(function (link) {
        const id = link.getAttribute('data-pltx2-faq-nav-link');
        if (id) linkById[id] = link;
      });

      let lastActiveId = null;
      let suppressRevealUntil = 0;
      let chipPointerDown = false;

      // Don't fight the user's finger while they swipe the chip row
      if (nav) {
        nav.addEventListener('pointerdown', function () { chipPointerDown = true; }, { passive: true });
        ['pointerup', 'pointercancel'].forEach(function (type) {
          document.addEventListener(type, function () { chipPointerDown = false; }, { passive: true });
        });
      }

      // Horizontally slide the chip row so the active chip is centered.
      // Viewport-delta + scrollBy is direction-agnostic (works in RTL) and
      // the browser clamps at both edges.
      function revealChip(link, force) {
        if (!link || !nav || !isChipMode()) return;
        if (!force && (chipPointerDown || Date.now() < suppressRevealUntil)) return;
        const navRect = nav.getBoundingClientRect();
        const linkRect = link.getBoundingClientRect();
        const delta = (linkRect.left + linkRect.width / 2) - (navRect.left + navRect.width / 2);
        if (Math.abs(delta) < 4) return;
        nav.scrollBy({ left: delta, behavior: reduceMotion ? 'auto' : 'smooth' });
      }

      function setActive(groupId) {
        if (groupId === lastActiveId) return;
        lastActiveId = groupId;
        navLinks.forEach(function (link) { link.classList.remove('is-active'); });
        const active = linkById[groupId];
        if (active) {
          active.classList.add('is-active');
          revealChip(active, false);
        }
      }

      // Reading line: just below the sticky overlay (header + chip bar on
      // mobile, header only on desktop). Using the sidebar's live rect means
      // the line is also correct before the bar becomes pinned.
      function readingLine() {
        let base = 0;
        if (isChipMode() && sidebar) {
          base = sidebar.getBoundingClientRect().bottom;
        } else if (themeHeader) {
          base = Math.max(0, themeHeader.getBoundingClientRect().bottom);
        }
        return base + 56;
      }

      // Hysteresis band: once a boundary flips the active chip, the crossing
      // must exceed this many pixels before it can flip back — kills the
      // rapid back-and-forth flicker from micro-scrolls at a boundary,
      // without adding perceptible latency (12px of scroll).
      const HYSTERESIS = 12;

      function computeActiveByScroll() {
        const visible = groups.filter(function (g) { return !g.hidden; });
        if (!visible.length) return;
        // At (or within 2px of) the page bottom the last group is the one
        // being read even if its top never crosses the reading line.
        const scroller = document.scrollingElement || document.documentElement;
        if (window.innerHeight + window.scrollY >= scroller.scrollHeight - 2) {
          setActive(visible[visible.length - 1].getAttribute('data-pltx2-faq-group'));
          return;
        }
        const line = readingLine();
        let candidate = null;
        for (let i = 0; i < visible.length; i++) {
          if (visible[i].getBoundingClientRect().top <= line) candidate = visible[i];
          else break;
        }
        if (!candidate) candidate = visible[0];
        const cid = candidate.getAttribute('data-pltx2-faq-group');
        if (cid !== lastActiveId && lastActiveId) {
          const lastEl = visible.find(function (g) {
            return g.getAttribute('data-pltx2-faq-group') === lastActiveId;
          });
          if (lastEl) {
            const candTop = candidate.getBoundingClientRect().top;
            const lastTop = lastEl.getBoundingClientRect().top;
            if (candTop > lastTop) {
              // switching forward: the next group must clear the line by the band
              if (candTop > line - HYSTERESIS) return;
            } else {
              // switching backward: the old group must clear the line by the band
              if (lastTop < line + HYSTERESIS) return;
            }
          }
        }
        setActive(cid);
      }

      let trackRaf = 0;
      let trackTimer = 0;
      function onTrack() {
        if (trackRaf || trackTimer) return;
        // rAF keeps updates frame-aligned when visible; the timeout is only
        // a fallback for hidden/occluded tabs where rAF is suspended.
        // Whichever fires first cancels its twin — a stale timeout can never
        // run between frames (that mistimed swap read as jitter on mobile).
        const run = function () {
          if (trackRaf) { cancelAnimationFrame(trackRaf); trackRaf = 0; }
          if (trackTimer) { clearTimeout(trackTimer); trackTimer = 0; }
          syncOverlayVars();
          computeActiveByScroll();
        };
        trackRaf = requestAnimationFrame(run);
        trackTimer = setTimeout(run, 120);
      }

      window.addEventListener('scroll', onTrack, { passive: true });
      window.addEventListener('resize', onTrack, { passive: true });
      if (chipModeMq.addEventListener) chipModeMq.addEventListener('change', onTrack);
      if ('ResizeObserver' in window) {
        const overlayRo = new ResizeObserver(onTrack);
        [themeHeader, sidebar].forEach(function (el) { if (el) overlayRo.observe(el); });
      }

      syncOverlayVars();
      computeActiveByScroll();

      // Expose for the click handler below
      root.__pltx2FaqSetActive = setActive;
      root.__pltx2FaqRevealChip = revealChip;
      root.__pltx2FaqSuppressReveal = function (ms) { suppressRevealUntil = Date.now() + ms; };
      root.__pltx2FaqRecompute = computeActiveByScroll;
    } else {
      syncOverlayVars();
      window.addEventListener('scroll', syncOverlayVars, { passive: true });
      window.addEventListener('resize', syncOverlayVars, { passive: true });
    }

    /* ---------- SMOOTH SCROLL for sidebar links ----------
       If search has filtered groups out, we clear the filter first so the
       target category is visible, then scroll to it. Otherwise clicking a
       sidebar link while searching appears to do nothing (the group is
       display:none'd). */
    navLinks.forEach(function (link) {
      link.addEventListener('click', function (e) {
        const href = link.getAttribute('href') || '';
        if (!href.startsWith('#')) return;
        const target = root.querySelector(href);
        if (!target) return;
        e.preventDefault();

        const hadSearch = !!(searchInput && searchInput.value);
        if (hadSearch) resetSearch();

        // Instant feedback: activate + center the tapped chip now, then keep
        // the chip row parked while the page glides (the scroll tracker keeps
        // updating the active class, but must not yank the row mid-flight).
        const gid = link.getAttribute('data-pltx2-faq-nav-link');
        if (gid && root.__pltx2FaqSetActive) {
          root.__pltx2FaqSetActive(gid);
          root.__pltx2FaqRevealChip(link, true);
          root.__pltx2FaqSuppressReveal(1200);
        }

        // If we just cleared search, wait one frame for the groups to be
        // un-hidden + laid out, then scroll
        const doScroll = function () {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          if (history.replaceState) history.replaceState(null, '', href);
        };
        if (hadSearch) {
          requestAnimationFrame(doScroll);
        } else {
          doScroll();
        }
      });
    });
  }

  function boot() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initFaq);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Shopify Theme Editor: re-init when the section is reloaded
  document.addEventListener('shopify:section:load', function (e) {
    const root = e.target.querySelector(ROOT_SELECTOR);
    if (root) initFaq(root);
  });
})();
